/** Tipos del bounded context Fiscal (ARCA/AFIP). */

export type TaxRegime = 'monotributo' | 'responsable_inscripto';

export type ArcaEnvironment = 'homologacion' | 'produccion';

export type FiscalSetupStatus = 'draft' | 'validated' | 'error';

/** Emisor org-wide (CUIT, régimen, cert, ambiente). */
export type FiscalProfile = {
  organizationId: string;
  taxRegime: TaxRegime;
  emitterCuit: string | null;
  businessName: string | null;
  /** Domicilio comercial del emisor — ARCA lo pide en el comprobante impreso. */
  domicilioComercial: string | null;
  /** IIBB: número, "Exento" o "Convenio Multilateral" — va en el comprobante. */
  iibb: string | null;
  /** Inicio de actividades ISO `YYYY-MM-DD` — va en el comprobante. */
  inicioActividades: string | null;
  monotributoCategory: string | null;
  environment: ArcaEnvironment;
  setupStatus: FiscalSetupStatus;
  setupValidatedAt: string | null;
  setupError: string | null;
  /** Flujo CSR (#1510): CUIT para el que hay un CSR pendiente de firma en ARCA. */
  arcaCsrCuit?: string | null;
  /** Ambiente para el que se generó el CSR pendiente. */
  arcaCsrEnvironment?: ArcaEnvironment | null;
  /** Última generación de CSR vía arca-csr-generate. */
  arcaCsrGeneratedAt?: string | null;
  /** Origen de la clave privada: generated (Vuelto) o manual (pegada). */
  arcaKeySource?: 'generated' | 'manual' | null;
  /** Vencimiento del certificado ARCA validado. */
  arcaCertExpiresAt?: string | null;
};

/** PV AFIP y validación WSFE por sucursal. */
export type BranchFiscalSettings = {
  branchId: string;
  organizationId: string;
  pointOfSale: number | null;
  pvValidatedAt: string | null;
  pvLastInvoiceNumber: number | null;
  pvSetupError: string | null;
};

export type BranchFiscalSummary = BranchFiscalSettings & {
  branchName: string;
};

export type FiscalContext = {
  profile: FiscalProfile | null;
  branchFiscal: BranchFiscalSettings | null;
  branchesFiscal?: BranchFiscalSummary[];
};

export type FiscalProfileInput = {
  taxRegime: TaxRegime;
  emitterCuit: string;
  businessName: string;
  /** Domicilio comercial (opcional; vacío se guarda como null). */
  domicilioComercial?: string | null;
  /** IIBB: número, "Exento" o "Convenio Multilateral" (opcional). */
  iibb?: string | null;
  /** Inicio de actividades ISO `YYYY-MM-DD` (otro formato se guarda como null). */
  inicioActividades?: string | null;
  monotributoCategory?: string | null;
  environment: ArcaEnvironment;
  /** PV de la sucursal activa; null lo limpia y undefined lo deja intacto. */
  pointOfSale?: number | null;
};

export type PadronResult = {
  cuit: string;
  razonSocial: string;
  taxCondition?: number | null;
  fromCache?: boolean;
  sandbox?: boolean;
};

export type PosFiscalBuyerDraft = {
  consumidorFinal: boolean;
  cuit: string;
  razonSocial: string;
  taxCondition: number | null;
  linkedCustomerId: string | null;
};

export function createEmptyFiscalBuyerDraft(): PosFiscalBuyerDraft {
  return {
    consumidorFinal: true,
    cuit: '',
    razonSocial: 'Consumidor final',
    taxCondition: null,
    linkedCustomerId: null,
  };
}

export type ArcaSetupValidateResult = {
  ok: boolean;
  razonSocial?: string;
  cuit?: string;
  fromCache?: boolean;
  setupStatus?: FiscalSetupStatus;
  lastInvoiceNumber?: number;
  pvValidated?: boolean;
  error?: string;
};

export type IvaRateOption = '21' | '10.5' | '27' | '0' | 'exento' | 'no_gravado';

/** Formato ISO de fecha `YYYY-MM-DD` (inicio de actividades del emisor). */
export function isValidIsoDateString(value: string | null | undefined): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim());
}

export function isBranchPvReady(ctx: FiscalContext | null): boolean {
  return Boolean(
    ctx?.profile?.setupStatus === 'validated' &&
      ctx.branchFiscal?.pointOfSale != null &&
      ctx.branchFiscal.pointOfSale > 0 &&
      ctx.branchFiscal.pvValidatedAt
  );
}
